/**
 * ISO 3166-1 alpha-2 codes, plus the exceptionally reserved AC and TA codes,
 * paired with their ITU country calling codes for the phone-verification picker.
 *
 * `iso` doubles as the flag asset name (/images/circle-flags/flags/<iso>.svg),
 * except where `flag` names a territory-specific or shared sovereign asset.
 * `dial` carries no leading '+': the picker renders it and the E.164 value is
 * composed as '+' + dial + national number.
 *
 * Territories sharing a calling code stay listed separately so the picker can
 * show the flag a user searches for. Their national destination code remains
 * part of the number field, where overlays are not tied to one area code.
 */
const PHONE_COUNTRY_CODES = [
    { iso: 'af', dial: '93', name: 'Afghanistan' },
    { iso: 'ax', dial: '358', name: 'Åland Islands' },
    { iso: 'al', dial: '355', name: 'Albania' },
    { iso: 'dz', dial: '213', name: 'Algeria' },
    { iso: 'as', dial: '1', name: 'American Samoa' },
    { iso: 'ad', dial: '376', name: 'Andorra' },
    { iso: 'ao', dial: '244', name: 'Angola' },
    { iso: 'ai', dial: '1', name: 'Anguilla' },
    { iso: 'ag', dial: '1', name: 'Antigua and Barbuda' },
    { iso: 'ar', dial: '54', name: 'Argentina', numberPlan: 'argentina' },
    { iso: 'am', dial: '374', name: 'Armenia' },
    { iso: 'aw', dial: '297', name: 'Aruba' },
    { iso: 'ac', dial: '247', name: 'Ascension Island', flag: 'sh-ac' },
    { iso: 'au', dial: '61', name: 'Australia' },
    { iso: 'at', dial: '43', name: 'Austria' },
    { iso: 'az', dial: '994', name: 'Azerbaijan' },
    { iso: 'bs', dial: '1', name: 'Bahamas' },
    { iso: 'bh', dial: '973', name: 'Bahrain' },
    { iso: 'bd', dial: '880', name: 'Bangladesh' },
    { iso: 'bb', dial: '1', name: 'Barbados' },
    { iso: 'by', dial: '375', name: 'Belarus', nationalPrefixes: ['80', '8', '0'], nationalLengths: [9] },
    { iso: 'be', dial: '32', name: 'Belgium' },
    { iso: 'bz', dial: '501', name: 'Belize' },
    { iso: 'bj', dial: '229', name: 'Benin', nationalPrefixSignificant: true },
    { iso: 'bm', dial: '1', name: 'Bermuda' },
    { iso: 'bt', dial: '975', name: 'Bhutan' },
    { iso: 'bo', dial: '591', name: 'Bolivia' },
    { iso: 'bq', dial: '599', name: 'Caribbean Netherlands' },
    { iso: 'ba', dial: '387', name: 'Bosnia and Herzegovina' },
    { iso: 'bw', dial: '267', name: 'Botswana' },
    { iso: 'br', dial: '55', name: 'Brazil', nationalPrefixes: ['0'], carrierSelectionPrefixes: ['90', '0'], carrierCodes: ['12', '14', '15', '21', '22', '23', '25', '31', '41', '43', '55', '65', '99'], nationalLengths: [10, 11] },
    { iso: 'io', dial: '246', name: 'British Indian Ocean Territory' },
    { iso: 'vg', dial: '1', name: 'British Virgin Islands' },
    { iso: 'bn', dial: '673', name: 'Brunei' },
    { iso: 'bg', dial: '359', name: 'Bulgaria' },
    { iso: 'bf', dial: '226', name: 'Burkina Faso', nationalPrefixSignificant: true },
    { iso: 'bi', dial: '257', name: 'Burundi' },
    { iso: 'kh', dial: '855', name: 'Cambodia' },
    { iso: 'cm', dial: '237', name: 'Cameroon' },
    { iso: 'ca', dial: '1', name: 'Canada' },
    { iso: 'cv', dial: '238', name: 'Cape Verde' },
    { iso: 'ky', dial: '1', name: 'Cayman Islands' },
    { iso: 'cf', dial: '236', name: 'Central African Republic' },
    { iso: 'td', dial: '235', name: 'Chad' },
    { iso: 'cl', dial: '56', name: 'Chile' },
    { iso: 'cn', dial: '86', name: 'China' },
    { iso: 'cx', dial: '61', name: 'Christmas Island' },
    { iso: 'cc', dial: '61', name: 'Cocos Islands' },
    { iso: 'co', dial: '57', name: 'Colombia' },
    { iso: 'km', dial: '269', name: 'Comoros' },
    { iso: 'cg', dial: '242', name: 'Congo - Brazzaville', nationalPrefixSignificant: true },
    { iso: 'cd', dial: '243', name: 'Congo - Kinshasa' },
    { iso: 'ck', dial: '682', name: 'Cook Islands' },
    { iso: 'cr', dial: '506', name: 'Costa Rica' },
    { iso: 'ci', dial: '225', name: 'Côte d’Ivoire', nationalPrefixSignificant: true },
    { iso: 'hr', dial: '385', name: 'Croatia' },
    { iso: 'cu', dial: '53', name: 'Cuba' },
    { iso: 'cw', dial: '599', name: 'Curaçao' },
    { iso: 'cy', dial: '357', name: 'Cyprus' },
    { iso: 'cz', dial: '420', name: 'Czechia' },
    { iso: 'dk', dial: '45', name: 'Denmark' },
    { iso: 'dj', dial: '253', name: 'Djibouti' },
    { iso: 'dm', dial: '1', name: 'Dominica' },
    { iso: 'do', dial: '1', name: 'Dominican Republic' },
    { iso: 'ec', dial: '593', name: 'Ecuador' },
    { iso: 'eg', dial: '20', name: 'Egypt' },
    { iso: 'sv', dial: '503', name: 'El Salvador' },
    { iso: 'gq', dial: '240', name: 'Equatorial Guinea' },
    { iso: 'er', dial: '291', name: 'Eritrea' },
    { iso: 'ee', dial: '372', name: 'Estonia' },
    { iso: 'sz', dial: '268', name: 'Eswatini' },
    { iso: 'et', dial: '251', name: 'Ethiopia' },
    { iso: 'fk', dial: '500', name: 'Falkland Islands' },
    { iso: 'fo', dial: '298', name: 'Faroe Islands' },
    { iso: 'fj', dial: '679', name: 'Fiji' },
    { iso: 'fi', dial: '358', name: 'Finland' },
    { iso: 'fr', dial: '33', name: 'France' },
    { iso: 'gf', dial: '594', name: 'French Guiana' },
    { iso: 'pf', dial: '689', name: 'French Polynesia' },
    { iso: 'ga', dial: '241', name: 'Gabon' },
    { iso: 'gm', dial: '220', name: 'Gambia' },
    { iso: 'ge', dial: '995', name: 'Georgia' },
    { iso: 'de', dial: '49', name: 'Germany' },
    { iso: 'gh', dial: '233', name: 'Ghana' },
    { iso: 'gi', dial: '350', name: 'Gibraltar' },
    { iso: 'gr', dial: '30', name: 'Greece' },
    { iso: 'gl', dial: '299', name: 'Greenland' },
    { iso: 'gd', dial: '1', name: 'Grenada' },
    { iso: 'gp', dial: '590', name: 'Guadeloupe' },
    { iso: 'gu', dial: '1', name: 'Guam' },
    { iso: 'gt', dial: '502', name: 'Guatemala' },
    { iso: 'gg', dial: '44', name: 'Guernsey' },
    { iso: 'gn', dial: '224', name: 'Guinea' },
    { iso: 'gw', dial: '245', name: 'Guinea-Bissau' },
    { iso: 'gy', dial: '592', name: 'Guyana' },
    { iso: 'ht', dial: '509', name: 'Haiti' },
    { iso: 'hn', dial: '504', name: 'Honduras' },
    { iso: 'hk', dial: '852', name: 'Hong Kong' },
    { iso: 'hu', dial: '36', name: 'Hungary', nationalPrefixes: ['06'], nationalLengths: [9] },
    { iso: 'is', dial: '354', name: 'Iceland' },
    { iso: 'in', dial: '91', name: 'India' },
    { iso: 'id', dial: '62', name: 'Indonesia' },
    { iso: 'ir', dial: '98', name: 'Iran' },
    { iso: 'iq', dial: '964', name: 'Iraq' },
    { iso: 'ie', dial: '353', name: 'Ireland' },
    { iso: 'im', dial: '44', name: 'Isle of Man' },
    { iso: 'il', dial: '972', name: 'Israel' },
    { iso: 'it', dial: '39', name: 'Italy', nationalPrefixSignificant: true },
    { iso: 'jm', dial: '1', name: 'Jamaica' },
    { iso: 'jp', dial: '81', name: 'Japan' },
    { iso: 'je', dial: '44', name: 'Jersey' },
    { iso: 'jo', dial: '962', name: 'Jordan' },
    { iso: 'kz', dial: '7', name: 'Kazakhstan', nationalPrefixes: ['8'], nationalLengths: [10] },
    { iso: 'ke', dial: '254', name: 'Kenya' },
    { iso: 'ki', dial: '686', name: 'Kiribati' },
    { iso: 'xk', dial: '383', name: 'Kosovo' },
    { iso: 'kw', dial: '965', name: 'Kuwait' },
    { iso: 'kg', dial: '996', name: 'Kyrgyzstan' },
    { iso: 'la', dial: '856', name: 'Laos' },
    { iso: 'lv', dial: '371', name: 'Latvia' },
    { iso: 'lb', dial: '961', name: 'Lebanon' },
    { iso: 'ls', dial: '266', name: 'Lesotho' },
    { iso: 'lr', dial: '231', name: 'Liberia' },
    { iso: 'ly', dial: '218', name: 'Libya' },
    { iso: 'li', dial: '423', name: 'Liechtenstein' },
    { iso: 'lt', dial: '370', name: 'Lithuania', nationalPrefixes: ['0', '8'], nationalLengths: [8] },
    { iso: 'lu', dial: '352', name: 'Luxembourg' },
    { iso: 'mo', dial: '853', name: 'Macao' },
    { iso: 'mg', dial: '261', name: 'Madagascar' },
    { iso: 'mw', dial: '265', name: 'Malawi' },
    { iso: 'my', dial: '60', name: 'Malaysia' },
    { iso: 'mv', dial: '960', name: 'Maldives' },
    { iso: 'ml', dial: '223', name: 'Mali' },
    { iso: 'mt', dial: '356', name: 'Malta' },
    { iso: 'mh', dial: '692', name: 'Marshall Islands' },
    { iso: 'mq', dial: '596', name: 'Martinique' },
    { iso: 'mr', dial: '222', name: 'Mauritania' },
    { iso: 'mu', dial: '230', name: 'Mauritius' },
    { iso: 'yt', dial: '262', name: 'Mayotte' },
    { iso: 'mx', dial: '52', name: 'Mexico' },
    { iso: 'fm', dial: '691', name: 'Micronesia' },
    { iso: 'md', dial: '373', name: 'Moldova' },
    { iso: 'mc', dial: '377', name: 'Monaco' },
    { iso: 'mn', dial: '976', name: 'Mongolia' },
    { iso: 'me', dial: '382', name: 'Montenegro' },
    { iso: 'ms', dial: '1', name: 'Montserrat' },
    { iso: 'ma', dial: '212', name: 'Morocco' },
    { iso: 'mz', dial: '258', name: 'Mozambique' },
    { iso: 'mm', dial: '95', name: 'Myanmar' },
    { iso: 'na', dial: '264', name: 'Namibia' },
    { iso: 'nr', dial: '674', name: 'Nauru' },
    { iso: 'np', dial: '977', name: 'Nepal' },
    { iso: 'nl', dial: '31', name: 'Netherlands' },
    { iso: 'nc', dial: '687', name: 'New Caledonia' },
    { iso: 'nz', dial: '64', name: 'New Zealand' },
    { iso: 'ni', dial: '505', name: 'Nicaragua' },
    { iso: 'ne', dial: '227', name: 'Niger' },
    { iso: 'ng', dial: '234', name: 'Nigeria' },
    { iso: 'nu', dial: '683', name: 'Niue' },
    { iso: 'nf', dial: '672', name: 'Norfolk Island' },
    { iso: 'kp', dial: '850', name: 'North Korea' },
    { iso: 'mk', dial: '389', name: 'North Macedonia' },
    { iso: 'mp', dial: '1', name: 'Northern Mariana Islands' },
    { iso: 'no', dial: '47', name: 'Norway' },
    { iso: 'om', dial: '968', name: 'Oman' },
    { iso: 'pk', dial: '92', name: 'Pakistan' },
    { iso: 'pw', dial: '680', name: 'Palau' },
    { iso: 'ps', dial: '970', name: 'Palestine' },
    { iso: 'pa', dial: '507', name: 'Panama' },
    { iso: 'pg', dial: '675', name: 'Papua New Guinea' },
    { iso: 'py', dial: '595', name: 'Paraguay' },
    { iso: 'pe', dial: '51', name: 'Peru' },
    { iso: 'ph', dial: '63', name: 'Philippines' },
    { iso: 'pl', dial: '48', name: 'Poland' },
    { iso: 'pt', dial: '351', name: 'Portugal' },
    { iso: 'pr', dial: '1', name: 'Puerto Rico' },
    { iso: 'qa', dial: '974', name: 'Qatar' },
    { iso: 're', dial: '262', name: 'Réunion' },
    { iso: 'ro', dial: '40', name: 'Romania' },
    { iso: 'ru', dial: '7', name: 'Russia', nationalPrefixes: ['8'], nationalLengths: [10] },
    { iso: 'rw', dial: '250', name: 'Rwanda' },
    { iso: 'bl', dial: '590', name: 'Saint Barthélemy' },
    { iso: 'sh', dial: '290', name: 'Saint Helena', flag: 'sh-hl' },
    { iso: 'kn', dial: '1', name: 'Saint Kitts and Nevis' },
    { iso: 'lc', dial: '1', name: 'Saint Lucia' },
    { iso: 'mf', dial: '590', name: 'Saint Martin' },
    { iso: 'pm', dial: '508', name: 'Saint Pierre and Miquelon' },
    { iso: 'vc', dial: '1', name: 'Saint Vincent and the Grenadines' },
    { iso: 'ws', dial: '685', name: 'Samoa' },
    { iso: 'sm', dial: '378', name: 'San Marino', nationalPrefixSignificant: true },
    { iso: 'st', dial: '239', name: 'São Tomé and Príncipe' },
    { iso: 'sa', dial: '966', name: 'Saudi Arabia' },
    { iso: 'sn', dial: '221', name: 'Senegal' },
    { iso: 'rs', dial: '381', name: 'Serbia' },
    { iso: 'sc', dial: '248', name: 'Seychelles' },
    { iso: 'sl', dial: '232', name: 'Sierra Leone' },
    { iso: 'sg', dial: '65', name: 'Singapore' },
    { iso: 'sx', dial: '1', name: 'Sint Maarten' },
    { iso: 'sk', dial: '421', name: 'Slovakia' },
    { iso: 'si', dial: '386', name: 'Slovenia' },
    { iso: 'sb', dial: '677', name: 'Solomon Islands' },
    { iso: 'so', dial: '252', name: 'Somalia' },
    { iso: 'za', dial: '27', name: 'South Africa' },
    { iso: 'kr', dial: '82', name: 'South Korea' },
    { iso: 'ss', dial: '211', name: 'South Sudan' },
    { iso: 'es', dial: '34', name: 'Spain' },
    { iso: 'lk', dial: '94', name: 'Sri Lanka' },
    { iso: 'sd', dial: '249', name: 'Sudan' },
    { iso: 'sr', dial: '597', name: 'Suriname' },
    { iso: 'sj', dial: '47', name: 'Svalbard and Jan Mayen', flag: 'no' },
    { iso: 'se', dial: '46', name: 'Sweden' },
    { iso: 'ch', dial: '41', name: 'Switzerland' },
    { iso: 'sy', dial: '963', name: 'Syria' },
    { iso: 'tw', dial: '886', name: 'Taiwan' },
    { iso: 'tj', dial: '992', name: 'Tajikistan', nationalPrefixSignificant: true },
    { iso: 'tz', dial: '255', name: 'Tanzania' },
    { iso: 'th', dial: '66', name: 'Thailand' },
    { iso: 'tl', dial: '670', name: 'Timor-Leste' },
    { iso: 'tg', dial: '228', name: 'Togo' },
    { iso: 'tk', dial: '690', name: 'Tokelau' },
    { iso: 'to', dial: '676', name: 'Tonga' },
    { iso: 'tt', dial: '1', name: 'Trinidad and Tobago' },
    { iso: 'ta', dial: '290', name: 'Tristan da Cunha', flag: 'sh-ta' },
    { iso: 'tn', dial: '216', name: 'Tunisia' },
    { iso: 'tr', dial: '90', name: 'Türkiye' },
    { iso: 'tm', dial: '993', name: 'Turkmenistan', nationalPrefixes: ['8'], nationalLengths: [8] },
    { iso: 'tc', dial: '1', name: 'Turks and Caicos Islands' },
    { iso: 'tv', dial: '688', name: 'Tuvalu' },
    { iso: 'ug', dial: '256', name: 'Uganda' },
    { iso: 'ua', dial: '380', name: 'Ukraine' },
    { iso: 'ae', dial: '971', name: 'United Arab Emirates' },
    { iso: 'gb', dial: '44', name: 'United Kingdom' },
    { iso: 'us', dial: '1', name: 'United States' },
    { iso: 'uy', dial: '598', name: 'Uruguay' },
    { iso: 'vi', dial: '1', name: 'U.S. Virgin Islands' },
    { iso: 'uz', dial: '998', name: 'Uzbekistan' },
    { iso: 'vu', dial: '678', name: 'Vanuatu' },
    // +379 is assigned to Vatican City but was never brought into service. Its
    // numbers sit in the Italian plan (+39 06 698...), so +379 reaches nobody.
    { iso: 'va', dial: '39', name: 'Vatican City', nationalPrefixSignificant: true },
    { iso: 've', dial: '58', name: 'Venezuela' },
    { iso: 'vn', dial: '84', name: 'Vietnam' },
    { iso: 'wf', dial: '681', name: 'Wallis and Futuna' },
    { iso: 'eh', dial: '212', name: 'Western Sahara' },
    { iso: 'ye', dial: '967', name: 'Yemen' },
    { iso: 'zm', dial: '260', name: 'Zambia' },
    { iso: 'zw', dial: '263', name: 'Zimbabwe' },
];

export default PHONE_COUNTRY_CODES;
